import { useState } from 'react';
import { colour, space, text } from '../design/tokens.ts';
import { Button, Card, Page, Status, Tag } from '../components/basics.tsx';
import { CouldNotLoad, DoneToast, Loading } from '../components/states.tsx';
import { api } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';
import { useCan } from '../app/me.tsx';

/**
 * Screen 5: this project's pipeline.
 *
 * The pipeline lives in the database and is always configurable. Nothing in
 * Maestro knows the name of any step: a step is classified by what the pipeline
 * says about it — `starts: when_assigned` marks the VC's own setup, and
 * `runnable: false` marks one Maestro records but never runs.
 *
 * Set up VC is fixed first and Edit is fixed last; everything between can move.
 * The server decides which, so Move up and Move down are simply absent where
 * they would not be allowed.
 */

interface Step {
  key: string;
  label: string;
  process: string;
  default_owner?: string;
  executor?: string;
  starts?: string;
  on_input_change?: 'mark_stale' | 'rerun';
  rerun_cap?: number;
  runnable?: boolean;
  required?: boolean;
  can_move_up?: boolean;
  can_move_down?: boolean;
}

interface PipelineAnswer {
  pipeline: { _id: string; name: string; is_default?: boolean; steps: Step[] };
  steps?: Step[];
}

export function Pipeline({ projectId }: { projectId: string }) {
  const can = useCan();
  const [toast, setToast] = useState<string | null>(null);

  const { state, reload } = useLoad<PipelineAnswer>(
    () => api.get(`/api/projects/${encodeURIComponent(projectId)}/pipeline`),
    [projectId]
  );
  const pipeline = state.data?.pipeline;
  const steps = state.data?.steps ?? pipeline?.steps ?? [];

  const move = async (key: string, direction: 'up' | 'down'): Promise<void> => {
    try {
      await api.post(`/api/projects/${encodeURIComponent(projectId)}/pipeline/steps/${key}/move`, {
        direction
      });
      setToast('Moved. The board columns follow this order.');
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That step cannot move there.');
    }
  };

  return (
    <Page
      eyebrow="MAESTRO · PIPELINE SETUP"
      title="Pipeline"
      description="The steps this project runs, in order. The board's columns come from here, and nothing in Maestro knows the name of any step."
      note={pipeline?.is_default ? 'This is the default pipeline, which every new project starts from.' : undefined}
    >
      {state.status === 'loading' && !pipeline && <Loading says="Loading the pipeline…" rows={5} />}
      {state.status === 'failed' && !pipeline && (
        <CouldNotLoad what="this pipeline" error={state.error} onRetry={reload} />
      )}

      {steps.map((step, i) => (
        <Card key={step.key}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control, alignItems: 'center' }}>
            <span
              style={{
                width: 32,
                height: 32,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 999,
                background: colour.surfaceSunk,
                border: `1px solid ${colour.edge}`,
                ...text.monoStrong
              }}
            >
              {i + 1}
            </span>
            <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.tight }}>
                <span style={text.bodyStrong}>{step.label}</span>
                <Tag tone="mono">{step.process}</Tag>
                {step.runnable === false && (
                  <Status value="notmade" detail="recorded, never run" />
                )}
                {step.starts === 'when_assigned' && <Tag>starts when assigned</Tag>}
                {step.required === false && <Tag>optional</Tag>}
              </span>
              <span style={{ ...text.note, color: colour.inkSoft }}>
                {step.default_owner ? `owned by ${step.default_owner}` : 'no default owner'}
                {step.executor ? ` · runs on ${step.executor}` : ''}
                {step.on_input_change
                  ? ` · when its input changes, ${step.on_input_change === 'rerun' ? 're-run automatically' : 'mark it stale'}`
                  : ''}
                {step.rerun_cap ? ` · up to ${step.rerun_cap} automatic attempts` : ''}
              </span>
            </div>
            {can.admin && (
              <div style={{ display: 'flex', gap: space.tight }}>
                <Button
                  onClick={() => void move(step.key, 'up')}
                  disabled={step.can_move_up === false || i === 0}
                  disabledReason="This step is fixed where it is"
                >
                  Move up
                </Button>
                <Button
                  onClick={() => void move(step.key, 'down')}
                  disabled={step.can_move_down === false || i === steps.length - 1}
                  disabledReason="This step is fixed where it is"
                >
                  Move down
                </Button>
              </div>
            )}
          </div>
        </Card>
      ))}

      <span style={{ ...text.note, color: colour.inkSoft }}>
        Set up VC is fixed first and Edit is fixed last. Everything between can move, and the board's
        columns follow whatever order this is in.
      </span>

      {toast && <DoneToast says={toast} onClose={() => setToast(null)} />}
    </Page>
  );
}
